/**
 * Finance / P&L page (A7Y-2054).
 *
 * Two tabs — Overview (totals + all-time category/tier breakdowns) and Monthly
 * (month-over-month series, newest first, each row linking to its detail page).
 * Data source: /admin/finance/pnl/summary served by a7y-api (Cloudflare
 * Workers). Revenue = Stripe balance transactions (prod, net USD); spend = the
 * "A7Y Ledger" Google Sheet. Both behind a 15-min / 5-min worker cache.
 */
import { useQuery } from "@tanstack/react-query";
import * as Tabs from "@radix-ui/react-tabs";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { OverviewTab } from "./components/OverviewTab";
import { MonthlyTab } from "./components/MonthlyTab";

// Mirror of the AutobuildPage tab styling so the finance tabs match the rest of
// the console.
const TAB_TRIGGER_CLASSES =
  "px-3 py-1.5 text-sm rounded-md text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-900 dark:data-[state=active]:text-zinc-100 data-[state=active]:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400";

// Worker caches revenue for 15 min and the ledger for 5 min; poll on the
// tighter of the two so the page stays reasonably fresh without over-fetching.
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export function PnlPage() {
  const summaryQ = useQuery({
    queryKey: ["finance-pnl", "summary"],
    queryFn: () => api.getPnlSummary(),
    refetchInterval: REFRESH_INTERVAL_MS,
  });

  const summary = summaryQ.data?.data;
  const hasData =
    summary != null &&
    (summary.monthly.length > 0 ||
      summary.totals.spend !== 0 ||
      summary.totals.revenue !== 0);

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="P & L"
        subtitle="Revenue (Stripe, net USD) vs. spend (A7Y Ledger) — all-time and month-over-month."
      >
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          {summaryQ.isFetching && (
            <span className="inline-flex items-center gap-1">
              <RefreshCw className="h-3 w-3 animate-spin" />
              refreshing…
            </span>
          )}
          <button
            type="button"
            onClick={() => summaryQ.refetch()}
            className="inline-flex items-center gap-1 rounded-md border border-zinc-200 dark:border-zinc-700 px-2 py-1 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50"
            disabled={summaryQ.isFetching}
          >
            <RefreshCw className="h-3 w-3" />
            Refresh
          </button>
        </div>
      </PageHeader>

      {summaryQ.isError && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-300">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          P&amp;L failed to load. Check the Worker logs for /admin/finance/pnl/summary.
        </div>
      )}

      {summaryQ.isLoading ? (
        <div className="p-6 text-sm text-zinc-500">Loading P&amp;L…</div>
      ) : !summary ? null : !hasData ? (
        <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6 text-sm text-zinc-500">
          No revenue or spend recorded yet.
        </div>
      ) : (
        <Tabs.Root defaultValue="overview">
          <Tabs.List className="mb-4 inline-flex items-center gap-1 rounded-lg bg-zinc-100 dark:bg-zinc-900 p-1">
            <Tabs.Trigger value="overview" className={TAB_TRIGGER_CLASSES}>
              Overview
            </Tabs.Trigger>
            <Tabs.Trigger value="monthly" className={TAB_TRIGGER_CLASSES}>
              Monthly
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="overview">
            <OverviewTab summary={summary} />
          </Tabs.Content>

          <Tabs.Content value="monthly">
            <MonthlyTab monthly={summary.monthly} />
          </Tabs.Content>
        </Tabs.Root>
      )}
    </div>
  );
}

export default PnlPage;
